import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import { shortenUnit, TinyTable } from '@rematter/ui-web';
import { MeasuredValue, Money } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideDot } from 'lucide-react';

import * as state from '../../_state/loadDetails.state';

const enumLoadWeightType = {
  Gross: 'gross',
  Tare: 'tare',
  Net: 'net',
} as const;

export const EntityLoadTabMaterials = () => {
  const {
    data: currentLoad,
    isLoading,
    error,
  } = useAtomValue(state.$loadDetails);

  const currentLoadLineItems = React.useMemo(
    () => currentLoad?.lineItems ?? [],
    [currentLoad],
  );

  return (
    <TinyTable
      className="@EntityLoadTabMaterials"
      isLoading={isLoading}
      error={error}
      data={currentLoadLineItems}
      rowKey={(lineItem) => lineItem.id}
      columns={[
        {
          label: 'Material',
          key: 'material',
          width: 2,
          cell: (lineItem) => (
            <div className="flex items-center gap-1.5">
              <span className="rounded-sm bg-neutral/10 px-[4px] py-[2px] text-xs">
                #{lineItem.item?.skuNumber}
              </span>
              <span>{lineItem.item?.name}</span>
            </div>
          ),
          footer: () => (
            <div className="flex items-center gap-0.5 font-semibold">
              <span>Total</span>
              <LucideDot size={16} className="opacity-50" />
              <span>{currentLoadLineItems.length} materials</span>
            </div>
          ),
        },
        {
          label: 'Net Weight',
          key: 'netWeight',
          align: 'right',
          cell: (lineItem) => (
            <MeasuredValue
              value={lineItem.weights?.[enumLoadWeightType.Net]?.value ?? 0}
              unit={
                lineItem.weights?.[enumLoadWeightType.Net]?.unit ??
                enumUnit.Pounds
              }
            />
          ),
          footer: () => (
            <div className="font-semibold">
              <MeasuredValue {...currentLoad?.netWeight} />
            </div>
          ),
        },
        {
          label: 'Unit Price',
          key: 'unitPrice',
          align: 'right',
          cell: (lineItem) => (
            <div>
              <Money {...lineItem.unitPrice} />
              {lineItem.weights?.[enumLoadWeightType.Net]?.unit && (
                <span className="ml-1 opacity-60">
                  / {shortenUnit(lineItem.weights[enumLoadWeightType.Net].unit)}
                </span>
              )}
            </div>
          ),
        },
        {
          label: 'Total Price',
          key: 'totalPrice',
          align: 'right',
          cell: (lineItem) => <Money {...lineItem.estimatedPrice} />,
          footer: () => (
            <div className="text-right font-semibold">
              <Money {...currentLoad?.totalPrice} />
            </div>
          ),
        },
      ]}
    />
  );
};
